import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type Category = Tables<"categories">;
export type Brand = Tables<"brands">;

export interface CategoryRow extends Category {
  /** Productos que la tienen asignada directamente. */
  productos: number;
  /** Subcategorias que cuelgan de ella. Solo las raices pueden tenerlas. */
  hijas: number;
}

export interface BrandRow extends Brand {
  productos: number;
}

/**
 * Enlace legible. El slug es unico por organizacion
 * (categories_organization_id_slug_key, brands_organization_id_slug_key), asi
 * que dos categorias con el mismo nombre chocan y hay que distinguirlas — por
 * eso «Alimentos» de perros y de gatos son `perros-alimentos` y
 * `gatos-alimentos` en los datos que ya existen.
 */
export function slugify(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Categorias con sus dos cuentas.
 *
 * Los totales se calculan aqui y no en PostgREST porque agrupar no entra en
 * su sintaxis de consulta sin crear una vista. Se traen solo las dos columnas
 * necesarias de products, que es un payload pequeno aunque el catalogo crezca.
 */
export async function fetchCategories(organizationId: string): Promise<CategoryRow[]> {
  const [cats, prods] = await Promise.all([
    supabase
      .from("categories")
      .select("*")
      .eq("organization_id", organizationId)
      .order("sort_order")
      .order("name"),
    supabase
      .from("products")
      .select("category_id")
      .eq("organization_id", organizationId)
      .is("archived_at", null),
  ]);

  if (cats.error) throw cats.error;
  if (prods.error) throw prods.error;

  const porCategoria = new Map<string, number>();
  for (const p of prods.data ?? []) {
    if (!p.category_id) continue;
    porCategoria.set(p.category_id, (porCategoria.get(p.category_id) ?? 0) + 1);
  }

  const hijasDe = new Map<string, number>();
  for (const c of cats.data ?? []) {
    if (!c.parent_id) continue;
    hijasDe.set(c.parent_id, (hijasDe.get(c.parent_id) ?? 0) + 1);
  }

  return (cats.data ?? []).map((c) => ({
    ...c,
    productos: porCategoria.get(c.id) ?? 0,
    hijas: hijasDe.get(c.id) ?? 0,
  }));
}

export interface CategoryDraft {
  name: string;
  slug: string;
  parent_id: string | null;
  active: boolean;
  sort_order: number;
}

export async function createCategory(organizationId: string, draft: CategoryDraft) {
  const { error } = await supabase
    .from("categories")
    .insert({ organization_id: organizationId, ...draft });
  if (error) throw error;
}

export async function updateCategory(id: string, draft: CategoryDraft) {
  const { error } = await supabase.from("categories").update(draft).eq("id", id);
  if (error) throw error;
}

/**
 * Borrar una categoria NO falla nunca: todas las claves que la referencian son
 * ON DELETE SET NULL (products.category_id, categories.parent_id,
 * product_spec_fields.category_id) o CASCADE (store_menu_item_categories).
 *
 * Es decir que se lleva por delante, en silencio, la clasificacion de sus
 * productos, el nivel de sus subcategorias y su entrada en el menu de la
 * tienda. Por eso la pantalla avisa con las cuentas antes de llamar aqui: la
 * base no va a poner ninguna objecion.
 */
export async function deleteCategory(id: string) {
  const { error } = await supabase.from("categories").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------------------ marcas */

export async function fetchBrands(organizationId: string): Promise<BrandRow[]> {
  const [marcas, prods] = await Promise.all([
    supabase
      .from("brands")
      .select("*")
      .eq("organization_id", organizationId)
      .order("sort_order")
      .order("name"),
    supabase
      .from("products")
      .select("brand_id")
      .eq("organization_id", organizationId)
      .is("archived_at", null),
  ]);

  if (marcas.error) throw marcas.error;
  if (prods.error) throw prods.error;

  const porMarca = new Map<string, number>();
  for (const p of prods.data ?? []) {
    if (!p.brand_id) continue;
    porMarca.set(p.brand_id, (porMarca.get(p.brand_id) ?? 0) + 1);
  }

  return (marcas.data ?? []).map((b) => ({ ...b, productos: porMarca.get(b.id) ?? 0 }));
}

export interface BrandDraft {
  name: string;
  slug: string;
  active: boolean;
  sort_order: number;
}

export async function createBrand(organizationId: string, draft: BrandDraft) {
  const { error } = await supabase
    .from("brands")
    .insert({ organization_id: organizationId, ...draft });
  if (error) throw error;
}

export async function updateBrand(id: string, draft: BrandDraft) {
  const { error } = await supabase.from("brands").update(draft).eq("id", id);
  if (error) throw error;
}

/** Igual que las categorias: products.brand_id es SET NULL, nunca falla. */
export async function deleteBrand(id: string) {
  const { error } = await supabase.from("brands").delete().eq("id", id);
  if (error) throw error;
}
